import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import requester from '@/lib/axios';
import Activity, { ActivityBankItem } from '@/models/Activity';
import { CourseLineSubCategory } from '@/models/CourseLine';
import JsonApiResponse from '@/models/JsonApiResponse';
import { OrderingFrom } from '@/models/Filter';
import { BaseFilters } from '@/models/Service';
import { QuestionTypeEnum } from '@/models/Question';
import { UserTypeEnum } from '@/models/User';

export async function getActivity(activityId: number): Promise<Activity> {
  const { data } = await requester().get<Activity>(`activities/${activityId}/`);
  return data;
}

interface ActivityBaseFilter extends BaseFilters {
  id?: number[];
  authorId?: number;
  authorUnitId?: number[];
  authorUserType?: UserTypeEnum[];
  courseAbbreviation?: string;
  areaId?: number[];
  tagId?: number[];
  tools?: number[];
  questionType?: QuestionTypeEnum[];
  questionCountMin?: number;
  questionCountMax?: number;
  inResource?: boolean;
}

export const ACTIVITY_ORDERING_FIELDS = ['scheduledlesson_count'] as const;

export type ActivityOrdering = OrderingFrom<typeof ACTIVITY_ORDERING_FIELDS>;

export interface ActivityFilter extends ActivityBaseFilter {
  lessonId?: number;
  course?: number;
  examId?: number;
  examAreaId?: number[];
  inBank?: boolean;
  isPublicInBank?: boolean;
  status?: VersioningStatusEnum[];
  ordering?: ActivityOrdering;
}
export async function listActivities(params: ActivityFilter = {}) {
  const { data } = await requester().get<JsonApiResponse<Activity>>(
    'activities/',
    {
      params,
    },
  );
  return data;
}

export async function listLessonActivities(
  lessonId: number,
  params: ActivityFilter = {},
) {
  const { data } = await requester().get<JsonApiResponse<Activity>>(
    `lessons/${lessonId}/activities/`,
    { params },
  );
  return data;
}

export interface ActivityBankFilter extends ActivityBaseFilter {
  course?: number[];
  courseId?: number[];
  lessonBlockId?: number[];
  lessonId?: number[];
  courseLineId?: number[];
  moduleId?: number[];
  subCategory?: CourseLineSubCategory[];
  questionAreaId?: number[];
}

export async function listActivityBank(params: ActivityBankFilter = {}) {
  const { data } = await requester().get<JsonApiResponse<ActivityBankItem>>(
    'activities/bank/',
    { params },
  );
  return data;
}

export interface UpdateActivityParams {
  activityId: number;
  lessonId: number;
}

export const deleteActivity = async ({
  activityId,
  lessonId,
}: UpdateActivityParams) => {
  const { data } = await requester().delete<Activity>(
    `lessons/${lessonId}/activities/${activityId}/`,
  );
  return data;
};

export const createActivity = async (
  lessonId: number,
  body: Partial<Activity>,
) => {
  const { data: newActivity } = await requester().post<Activity>(
    `lessons/${lessonId}/activities/`,
    body,
  );
  return newActivity;
};

export const removeActivityFromLesson = async ({
  lessonId,
  activityId,
}: UpdateActivityParams) => {
  await requester().delete(
    `lessons/${lessonId}/remove-activity/${activityId}/`,
  );
};

export const updateActivityFromLesson = async (
  { activityId, lessonId }: UpdateActivityParams,
  activity: Partial<Activity> = {},
) => {
  const { data } = await requester().patch<Activity>(
    `lessons/${lessonId}/activities/${activityId}/`,
    activity,
  );
  return data;
};

export type ActivityQuestionOrder = {
  id: number;
  questions: Array<{
    id: number;
    identifier: string;
    type: QuestionTypeEnum;
  }>;
};

export const updateOrderQuestions = async (
  lessonId: number,
  body: ActivityQuestionOrder[],
) => {
  await requester().patch<void>(
    `lessons/${lessonId}/activities/questions/order/`,
    body,
  );
};
